import { FC, useState, useEffect } from 'react';
import { Bell, BellOff, Smartphone, Settings } from 'lucide-react';
import { Link } from 'react-router-dom';
import { InvestorProfile } from '@/types/index';
import { pushNotificationService } from '@services/push-notification.service';

interface Props {
  profile: Partial<InvestorProfile>;
  onChange: (data: Partial<InvestorProfile>) => void;
}

const FREQUENCES = [
  { value: 'NEVER',     label: 'Jamais',    icon: '🔕', desc: 'Pas de notification email' },
  { value: 'IMMEDIATE', label: 'Immédiat',  icon: '⚡', desc: 'Dès qu\'une opportunité est détectée' },
  { value: 'DAILY',     label: 'Quotidien', icon: '📅', desc: 'Résumé chaque matin' },
  { value: 'WEEKLY',    label: 'Hebdo',     icon: '📆', desc: 'Résumé chaque lundi' },
];

export const Step6Notifications: FC<Props> = ({ profile, onChange }) => {
  const freq       = profile.notifFrequence ?? 'NEVER';
  const scoreMin   = profile.scoreMin ?? 70;
  const strategies = (profile.strategies as string[] | undefined) ?? [];
  const hasLocatif = strategies.includes('LOCATIF') || strategies.length === 0;
  const hasFlip    = strategies.includes('FLIP')    || strategies.length === 0;
  const flipNiveau = profile.alertFlipNiveau ?? 'GREEN';

  const [pushSupported, setPushSupported]   = useState(false);
  const [pushPermission, setPushPermission] = useState<NotificationPermission>('default');
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const [pushLoading, setPushLoading]       = useState(false);

  useEffect(() => {
    if (!pushNotificationService.isSupported()) return;
    setPushSupported(true);
    setPushPermission(Notification.permission);
    pushNotificationService.isSubscribed().then(setPushSubscribed);
    pushNotificationService.register();
  }, []);

  const handleTogglePush = async () => {
    setPushLoading(true);
    try {
      if (pushSubscribed) {
        await pushNotificationService.unsubscribe();
        setPushSubscribed(false);
        setPushPermission(Notification.permission);
      } else {
        const ok = await pushNotificationService.subscribe();
        if (ok) {
          setPushSubscribed(true);
          setPushPermission('granted');
        } else {
          setPushPermission(Notification.permission);
        }
      }
    } catch (err) {
      console.error('[Push] Erreur souscription:', err);
    } finally {
      setPushLoading(false);
    }
  };

  const rendMin = profile.rejectionMinYield ?? null;
  const cfMin   = profile.cashflowMin ?? null;
  const noLocatifThresholds = rendMin === null && cfMin === null;

  return (
    <div className="space-y-8">
      {/* Fréquence email */}
      <div>
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1 flex items-center gap-2">
          <Bell className="w-4 h-4" /> Alertes email
        </h3>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
          Recevez un email dès qu'un bien correspond à votre profil.
        </p>
        <div className="grid grid-cols-2 gap-3">
          {FREQUENCES.map(f => (
            <button
              key={f.value}
              onClick={() => onChange({ notifFrequence: f.value as InvestorProfile['notifFrequence'] })}
              className={`p-4 rounded-xl border-2 text-left transition-all
                ${freq === f.value
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                  : 'border-gray-200 dark:border-gray-700 hover:border-blue-300'}`}
            >
              <div className="text-2xl mb-1">{f.icon}</div>
              <div className="font-semibold text-sm text-gray-900 dark:text-gray-100">{f.label}</div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{f.desc}</div>
            </button>
          ))}
        </div>
      </div>

      {freq !== 'NEVER' && (
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
            Critères de déclenchement
          </h3>

          {/* Bloc LOCATIF */}
          {hasLocatif && (
            <div className="p-4 rounded-2xl border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/10 space-y-3">
              <p className="text-sm font-semibold text-blue-700 dark:text-blue-400">
                🏘️ Achat pour louer
              </p>
              <p className="text-xs text-blue-600 dark:text-blue-400">
                Une alerte est déclenchée si <strong>l'un des critères suivants</strong> est atteint :
              </p>

              {noLocatifThresholds ? (
                <div className="text-xs text-gray-500 dark:text-gray-400 space-y-1">
                  <p>Aucun seuil locatif configuré — le score global est utilisé (≥ {scoreMin}).</p>
                  <Link
                    to="/profile?tab=investor"
                    className="inline-flex items-center gap-1 text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    <Settings className="w-3 h-3" />
                    Configurer les seuils dans Étape 5
                  </Link>
                </div>
              ) : (
                <div className="space-y-1 text-xs text-gray-700 dark:text-gray-300">
                  {rendMin !== null && (
                    <div className="flex items-center gap-2">
                      <span className="text-green-600 dark:text-green-400 font-bold">✓</span>
                      <span>Rendement brut ≥ <strong>{rendMin}%</strong></span>
                    </div>
                  )}
                  {cfMin !== null && (
                    <div className="flex items-center gap-2">
                      <span className="text-green-600 dark:text-green-400 font-bold">✓</span>
                      <span>Cash-flow estimé ≥ <strong>{cfMin} CHF/mois</strong></span>
                    </div>
                  )}
                  <Link
                    to="/profile?tab=investor"
                    className="inline-flex items-center gap-1 text-blue-600 dark:text-blue-400 hover:underline mt-1"
                  >
                    <Settings className="w-3 h-3" />
                    Modifier dans Profil investisseur → Étape 5
                  </Link>
                </div>
              )}
            </div>
          )}

          {/* Bloc FLIP */}
          {hasFlip && (
            <div className="p-4 rounded-2xl border border-orange-200 dark:border-orange-800 bg-orange-50 dark:bg-orange-900/10 space-y-3">
              <p className="text-sm font-semibold text-orange-700 dark:text-orange-400">
                🔨 Achat · rénovation · revente
              </p>
              <p className="text-xs text-orange-600 dark:text-orange-400">
                Une alerte est déclenchée selon la viabilité flip du bien :
              </p>
              <div className="space-y-2">
                {([
                  { value: 'GREEN',        label: 'Points verts uniquement',      desc: 'Prix demandé ≤ prix d\'achat max calculé' },
                  { value: 'GREEN_ORANGE', label: 'Points verts et oranges',      desc: 'Dépassement du prix max ≤ 20%' },
                ] as const).map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => onChange({ alertFlipNiveau: opt.value })}
                    className={`w-full p-3 rounded-lg border-2 text-left text-sm transition-all
                      ${flipNiveau === opt.value
                        ? 'border-orange-500 bg-orange-100 dark:bg-orange-900/30'
                        : 'border-gray-200 dark:border-gray-700 hover:border-orange-300 bg-white dark:bg-gray-800'}`}
                  >
                    <span className="font-medium text-gray-900 dark:text-gray-100">{opt.label}</span>
                    <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">{opt.desc}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Score global — filet de sécurité (accordéon) */}
          <details className="group">
            <summary className="cursor-pointer text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 select-none list-none flex items-center gap-1">
              <span className="group-open:hidden">▶</span>
              <span className="hidden group-open:inline">▼</span>
              Réglage avancé — score global
            </summary>
            <div className="mt-3 space-y-2">
              <p className="text-xs text-gray-400">
                Utilisé uniquement si aucun seuil de stratégie n'est configuré.
              </p>
              <div className="flex items-center justify-between text-sm font-medium text-gray-700 dark:text-gray-300">
                <span>Score minimum</span>
                <span className="font-bold text-blue-600">{scoreMin}</span>
              </div>
              <input
                type="range"
                min="50"
                max="95"
                step="5"
                value={scoreMin}
                onChange={e => onChange({ scoreMin: parseInt(e.target.value) })}
                className="w-full h-2 rounded-full accent-blue-500"
              />
              <div className="flex justify-between text-xs text-gray-400">
                <span>50 (large)</span>
                <span className="text-emerald-600 font-medium">85 = OPPORTUNITY</span>
                <span>95 (strict)</span>
              </div>
            </div>
          </details>
        </div>
      )}

      {/* Notifications push */}
      <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1 flex items-center gap-2">
          <Smartphone className="w-4 h-4" /> Notifications push (mobile)
        </h3>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
          Recevez des notifications directement sur votre téléphone, même sans ouvrir votre email.
          Fonctionne sur Android (Chrome) et iOS 16.4+ (Safari, après ajout à l'écran d'accueil).
        </p>

        {!pushSupported ? (
          <div className="flex items-center gap-2 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg text-sm text-gray-500">
            <BellOff className="w-4 h-4 flex-shrink-0" />
            <span>Les notifications push ne sont pas supportées par votre navigateur.</span>
          </div>
        ) : pushPermission === 'denied' ? (
          <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg text-sm text-red-600 dark:text-red-400">
            <BellOff className="w-4 h-4 flex-shrink-0" />
            <span>Les notifications sont bloquées. Autorisez-les dans les paramètres de votre navigateur.</span>
          </div>
        ) : (
          <button
            onClick={handleTogglePush}
            disabled={pushLoading}
            className={`flex items-center gap-3 px-4 py-3 rounded-xl border-2 transition-all w-full text-left
              ${pushSubscribed
                ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300'
                : 'border-gray-200 dark:border-gray-700 hover:border-blue-300 text-gray-700 dark:text-gray-300'
              } ${pushLoading ? 'opacity-60 cursor-not-allowed' : ''}`}
          >
            {pushSubscribed
              ? <Bell className="w-5 h-5 text-emerald-600" />
              : <BellOff className="w-5 h-5 text-gray-400" />}
            <div>
              <div className="font-semibold text-sm">
                {pushSubscribed ? 'Notifications push activées' : 'Activer les notifications push'}
              </div>
              <div className="text-xs opacity-70 mt-0.5">
                {pushSubscribed
                  ? 'Cliquez pour désactiver les notifications sur cet appareil'
                  : 'Votre navigateur vous demandera l\'autorisation'}
              </div>
            </div>
          </button>
        )}
      </div>

      {freq !== 'NEVER' && (
        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-200 dark:border-blue-800">
          <p className="text-sm text-blue-800 dark:text-blue-300">
            Les alertes email seront envoyées à votre adresse de connexion.<br/>
            Le lien dans l'email vous amène directement à la fiche du bien.
          </p>
        </div>
      )}
    </div>
  );
};
